/**
 * CSS selectors and class names used by the accordion module.
 * @readonly
 * @type {{
 *   itemSelector: string,
 *   panelSelector: string,
 *   activeClass: string,
 *   openClass: string
 * }}
 */
const SELECTORS = {
  itemSelector: '.accordion__item',
  panelSelector: '.accordion__content',
  activeClass: 'accordion__item--is-active',
  openClass: 'accordion__item--open',
};

const { itemSelector, panelSelector, activeClass, openClass } = SELECTORS;

/**
 * Hides all accordion panels.
 *
 * @param {HTMLElement[]} panels - Array of panel elements to hide.
 * @returns {void}
 */
function hideAllPanels(panels) {
  panels.forEach((panel) => {
    panel.style.display = 'none';
  });
}

/**
 * Closes a single active panel and removes the active class
 * from its associated item (the previous *element* sibling).
 *
 * @param {HTMLElement} panel - The panel element to close.
 * @returns {void}
 */
function closeActivePanel(panel) {
  panel.style.display = 'none';
  const previousItem = panel.previousSibling?.previousSibling;
  if (previousItem?.classList.contains(activeClass)) {
    previousItem.classList.remove(activeClass);
  }
}

/**
 * Closes all currently open panels from a given list.
 *
 * @param {HTMLElement[]} panels - Array of panel elements to evaluate/close.
 * @returns {void}
 */
function closeAllPanels(panels) {
  panels.forEach((panel) => {
    if (panel.style.display === 'block') {
      closeActivePanel(panel);
    }
  });
}

/**
 * Toggles the active state of an accordion item and its associated panel.
 *
 * - If the item is active, it will be deactivated and its panel hidden.
 * - If the item is inactive, all other open panels will be closed and this one opened.
 *
 * @param {HTMLElement} item - The accordion item to toggle.
 * @param {HTMLElement[]} panels - All accordion panels in the same group.
 * @returns {void}
 */
function toggleAccordionItem(item, panels) {
  const isActive = item.classList.contains(activeClass);

  if (isActive) {
    item.classList.remove(activeClass);
    item.nextElementSibling.style.display = 'none';
  } else {
    item.classList.add(activeClass);
    closeAllPanels(panels);
    item.nextElementSibling.style.display = 'block';
  }
}

/**
 * Attaches click listeners to accordion items to toggle their panels.
 *
 * @param {HTMLElement[]} items - All accordion item elements.
 * @param {HTMLElement[]} panels - All accordion panel elements.
 * @returns {void}
 */
function setupAccordionListeners(items, panels) {
  items.forEach((item) => {
    item.addEventListener('click', () => toggleAccordionItem(item, panels));
  });
}

/**
 * Opens any accordion item that has the "default open" class applied.
 * Uses {@link SELECTORS.openClass}.
 *
 * @returns {void}
 */
function openDefaultAccordion() {
  const defaultOpenItem = document.querySelector(`.${openClass}`);
  if (defaultOpenItem) {
    defaultOpenItem.classList.add(activeClass);
    defaultOpenItem.nextElementSibling.style.display = 'block';
  }
}

/**
 * Initialises all accordions on the page:
 * - hides all panels
 * - attaches listeners
 * - opens any item marked with the default open class
 *
 * Safe to call multiple times (listeners are added each call, so prefer to call after DOM updates).
 *
 * @function init
 * @returns {void}
 */
function init() {
  const panels = Array.from(document.querySelectorAll(panelSelector));
  const items = Array.from(document.querySelectorAll(itemSelector));

  hideAllPanels(panels);
  setupAccordionListeners(items, panels);
  openDefaultAccordion();
}

/**
 * Global namespace for accordion API.
 * @namespace
 * @property {() => void} init - Re-initialises the accordion behavior on the current DOM.
 */
window.Accordion = window.Accordion || {};
window.Accordion.init = init;

/**
 * Auto-initialise on page load:
 * - If a global `vm.onload` hook exists, use it.
 * - Otherwise, fall back to DOMContentLoaded.
 */
if (window.vm) {
  vm.onload(init);
} else {
  window.addEventListener('DOMContentLoaded', init);
}
